'use client'

import { useQueries } from '@tanstack/react-query'
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai'
import { KeyboardIcon, TriangleIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { exchangeDisplayName } from '@/components/widget/market-status-badge'
import {
  StatusBarTabGroups,
  useStatusBarTabGroups,
} from '@/components/widget/status-bar-tab-groups'
import { useAuthGuard } from '@/features/auth/hooks/use-auth-guard'
import { watchlistEntryKey } from '@/features/dashboard/lib/watchlist-entry'
import { openAssistant } from '@/features/order/chat/lib/open-assistant'
import { openOrderFormSheet } from '@/features/order/order-form/lib/merged-order-sheet'
import { resolveActiveCardId, useCardAccountContext } from '@/hooks/use-card-account'
import { useNumberFormat } from '@/hooks/use-number-format'
import { usePriceFlash } from '@/hooks/use-price-flash'
import { DEFAULT_VENUE_EXCHANGE } from '@/lib/configs/default-venue'
import { getExchangeIconKey } from '@/lib/configs/exchange-registry'
import { pricePrefix, venueQuoteCurrency } from '@/lib/currency-format'
import { cn } from '@/lib/utils'
import { displayPriceOf, midPriceOf } from '@/lib/utils/display-price'
import { formatSymbol } from '@/lib/utils/format-util'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { instrumentListQueryOptions, useInstrumentList } from '@/service/instrument.hook'
import { useGetPageLayout, useGetPages } from '@/service/ui-state.hook'
import { useEnabledExchangeList } from '@/service/venue.hook'
import { useGetWatchlists } from '@/service/watchlist.hook'
import { useMultiMarketDataStream } from '@/service/ws/use-market-data-stream'
import { activePageIdAtom } from '@/state/dashboard-layouts'
import {
  lastFocusedOrderCardAtom,
  orderFormPopupAnchorAtom,
  orderFormPopupCardIdAtom,
  registeredOrderContainersAtom,
} from '@/state/order'
import { hideBalancesAtom, pageTabsOrderAtom } from '@/state/storage'
import { watchlistActiveIdAtomFamily } from '@/state/widget-instances'
import {
  Exchange,
  IncrementalTicker,
  InstrumentKind,
  MarketDataType,
  Ticker,
  TickerList,
} from '@/types/generated/common/types'

// ─── Sub-components ───────────────────────────────────────────────────────────

const IS_DEBUG_ALLOWED = process.env.NEXT_PUBLIC_ENV !== 'prod'

// The status bar leads with the bid/ask mid (steadier than a sparse last trade), then the
// instrument's own last/mark — never its index, which read as the price jumping around.
function statusBarPriceOf(ticker: Parameters<typeof displayPriceOf>[0]): string | undefined {
  return (ticker && midPriceOf(ticker)) ?? displayPriceOf(ticker)
}

export function useStatusBarShortcuts() {
  const router = useRouter()
  const { isLoggedIn } = useAuthGuard()
  const lastFocusedCard = useAtomValue(lastFocusedOrderCardAtom)
  const [popupCardId, setPopupCardId] = useAtom(orderFormPopupCardIdAtom)
  const setAnchorRect = useSetAtom(orderFormPopupAnchorAtom)
  const store = useStore()
  const { data: pagesData } = useGetPages({ enabled: isLoggedIn })
  const [pageTabsOrder] = useAtom(pageTabsOrderAtom)
  const [hideBalances, setHideBalances] = useAtom(hideBalancesAtom)

  const orderedPages = useMemo(() => {
    const pages = pagesData?.pages ?? []
    const sorted = [...pages].sort((a, b) => Number(a.createdAt) - Number(b.createdAt))
    if (!pageTabsOrder.length) return sorted
    const orderMap = new Map(pageTabsOrder.map((id, i) => [id, i]))
    return [...sorted].sort((a, b) => {
      const ai = orderMap.get(a.pageId) ?? Number.MAX_SAFE_INTEGER
      const bi = orderMap.get(b.pageId) ?? Number.MAX_SAFE_INTEGER
      return ai - bi
    })
  }, [pagesData, pageTabsOrder])

  const toggleHideBalances = useCallback(() => setHideBalances((v) => !v), [setHideBalances])

  // Opens even with an Order Form widget on the page — then on a copy of every group's rows
  // (see openOrderFormSheet).
  const openPopup = useCallback(
    () => openOrderFormSheet(store, lastFocusedCard),
    [store, lastFocusedCard]
  )

  const openChat = useCallback(() => openAssistant(store), [store])

  const hidePopup = useCallback(() => {
    setPopupCardId(null)
    setAnchorRect(null)
  }, [setPopupCardId, setAnchorRect])

  const openPage = useCallback(
    (pageId: string) => {
      router.push(`/trade/${pageId}`)
    },
    [router]
  )

  return {
    hideBalances,
    hidePopup,
    openChat,
    openPage,
    openPopup,
    orderedPages,
    popupCardId,
    toggleHideBalances,
  }
}

function KeyboardShortcutButton() {
  const t = useTranslations('trade')
  const commonT = useTranslations('common')
  const {
    hideBalances,
    hidePopup,
    openChat,
    openPage,
    openPopup,
    orderedPages,
    popupCardId,
    toggleHideBalances,
  } = useStatusBarShortcuts()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="hidden md:flex" asChild>
        <button
          type="button"
          className="text-foreground/50 hover:text-foreground/60 bg-foreground/5 flex items-center gap-1.5 px-2 py-0.5 text-xs transition-colors"
        >
          <KeyboardIcon className="size-3" />
          <span>{t('shortcuts')}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuItem onClick={openChat}>
          {t('openSheetChat')}
          <DropdownMenuShortcut>C</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openPopup}>
          {t('openSheetOrderForm')}
          <DropdownMenuShortcut>O</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={hidePopup} disabled={popupCardId === null}>
          {t('hideSheet')}
          <DropdownMenuShortcut>H</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={toggleHideBalances}>
          {hideBalances ? commonT('showBalances') : commonT('hideBalances')}
          <DropdownMenuShortcut>B</DropdownMenuShortcut>
        </DropdownMenuItem>

        {orderedPages.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {orderedPages.slice(0, 9).map((page, i) => (
              <DropdownMenuItem key={page.pageId} onClick={() => openPage(page.pageId)}>
                {page.name}
                <DropdownMenuShortcut>{i + 1}</DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
          </>
        )}

        {IS_DEBUG_ALLOWED && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="opacity-40">
              {t('toggleDebugMode')}
              <DropdownMenuShortcut>⌘⇧\</DropdownMenuShortcut>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function PriceChangeDisplay({
  currentPrice,
  className,
}: {
  currentPrice?: string
  className?: string
}) {
  const baseColor = 'text-white'
  const fmt = useNumberFormat()
  const current = currentPrice === undefined ? null : Number(currentPrice || 0)

  // Format prices consistently to 2 decimals so the row doesn't shift width as prices tick
  const formattedCurrent =
    current === null
      ? undefined
      : fmt.number(current, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const { direction, flashKey, prevValue } = usePriceFlash(current)

  if (formattedCurrent === undefined) {
    return <span className={cn(baseColor, className)}>-</span>
  }

  // No tick to diff against yet — show all in white, un-animated
  if (direction === null || prevValue === null) {
    return <span className={cn(baseColor, 'tabular-nums', className)}>{formattedCurrent}</span>
  }

  const formattedPrev = fmt.number(prevValue, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  // Find first differing character index
  let diffIndex = 0
  const minLen = Math.min(formattedCurrent.length, formattedPrev.length)
  for (let i = 0; i < minLen; i++) {
    if (formattedCurrent[i] !== formattedPrev[i]) {
      diffIndex = i
      break
    }
    diffIndex = i + 1
  }

  if (diffIndex >= minLen && formattedCurrent.length !== formattedPrev.length) {
    diffIndex = minLen
  }

  const unchangedPart = formattedCurrent.slice(0, diffIndex)
  const changedPart = formattedCurrent.slice(diffIndex)

  return (
    <span className={cn('tracking-tight tabular-nums', className)}>
      <span className={baseColor}>{unchangedPart}</span>
      <span
        key={flashKey}
        className={cn(
          baseColor,
          direction === 'up' && 'animate-[price-flash-up_550ms]',
          direction === 'down' && 'animate-[price-flash-down_550ms]'
        )}
      >
        {changedPart}
      </span>
    </span>
  )
}

function AssetPriceItem({
  symbol,
  exchange,
  tickerData,
}: {
  symbol: string
  exchange: Exchange
  tickerData: { current: IncrementalTicker | Ticker; prev?: string } | undefined
}) {
  const exchangeName = exchangeDisplayName(exchange)
  const exchangeIconKey = getExchangeIconKey(exchange) ?? exchangeName
  const ticker = tickerData?.current as any
  const currentPrice = statusBarPriceOf(ticker)
  const prevPrice = tickerData?.prev

  const changeRaw =
    (ticker as any)?.stats?.priceChangePercent ?? (ticker as any)?.priceChange24h ?? '0'
  const change24h = Number(changeRaw)

  // Use tick direction for triangle, fallback to 24h change
  const currentVal = Number(currentPrice || 0)
  const prevVal = Number(prevPrice || currentPrice || 0)
  const isTickActive = prevPrice !== undefined && currentPrice !== prevPrice
  const isTickUp = currentVal > prevVal
  const is24hUp = change24h >= 0

  const activeUp = isTickActive ? isTickUp : is24hUp
  const activeColor = activeUp ? 'text-chart-mint' : 'text-chart-red'
  const activeFill = activeUp ? 'fill-chart-mint' : 'fill-chart-red'

  return (
    <div className="flex shrink-0 cursor-default items-center gap-1.5 px-3">
      <ExchangeIcon
        exchange={exchangeIconKey}
        className="size-4 shrink-0 rounded-full"
        logoObjectFit="contain"
        fallback={
          <div className="flex h-full w-full items-center justify-center rounded-full bg-white/10 text-[8px] text-white/50">
            {exchangeName[0]}
          </div>
        }
      />
      <span className="text-foreground/40 hidden text-[11px] tracking-tight md:inline">
        {formatSymbol(symbol)}
      </span>
      <span className="font-sans text-xs font-medium tracking-tight">
        {/* Not a hardcoded '$': every KRX security is quoted in won, so the
            exchange alone answers this — no per-item instrument lookup. Every
            other venue still falls back to '$'. */}
        <span className="text-white">{pricePrefix(venueQuoteCurrency(exchange))}</span>
        <PriceChangeDisplay currentPrice={currentPrice} />
      </span>
      <span className={cn('text-[9px] leading-none', activeColor)}>
        <TriangleIcon
          className={cn(
            'size-2 transition-transform duration-300',
            activeFill,
            !activeUp && 'rotate-180'
          )}
        />
      </span>
    </div>
  )
}

interface AssetPriceRowProps {
  items: PriceRowInstrument[]
  tickerMap: Map<string, { current: IncrementalTicker | Ticker; prev?: string }>
}

// Static (no auto-scroll): the fallback when the page has no tab groups, e.g. a fresh page.
// Overflow scrolls by hand.
function AssetPriceRow({ items, tickerMap }: AssetPriceRowProps) {
  return (
    <div className="scrollbar-none flex h-full min-w-0 flex-1 items-center overflow-x-auto whitespace-nowrap">
      {items.map((asset) => {
        const key = watchlistEntryKey(asset.exchange, asset.symbol)
        return (
          <AssetPriceItem
            key={key}
            symbol={asset.symbol}
            exchange={asset.exchange}
            tickerData={tickerMap.get(key)}
          />
        )
      })}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

const PRICE_ROW_FLUSH_MS = 1000
const WATCHLIST_ROW_ITEM_COUNT = 10
const STATUS_BAR_FALLBACK_WATCHLIST_ID = '__status_bar__'

type PriceRowInstrument = {
  symbol: string
  exchange: Exchange
}

const NO_PRICE_ROW_ITEMS: PriceRowInstrument[] = []

/**
 * The fixed bottom bar: the keyboard-shortcuts menu, then the page's tab groups (switch, reorder
 * and move tabs across groups), or a static price row on a page with no tabs. Connection status
 * and support/social links live in the side panel (TradeSidePanel) and the mobile nav sheet.
 */
export function StatusBar({
  placement = 'viewport',
}: {
  /**
   * `viewport`: the phone layout's full-width bar fixed to the screen bottom. `card`: the
   * desktop's 32px bar along the bottom of the content column (exchange-tab-group.tsx).
   */
  placement?: 'viewport' | 'card'
}) {
  const t = useTranslations('trade')
  const lastFocusedCard = useAtomValue(lastFocusedOrderCardAtom)
  const registeredContainers = useAtomValue(registeredOrderContainersAtom)
  const activeCardId = resolveActiveCardId(lastFocusedCard, registeredContainers)
  const { tradeVenue: activeCardTradeVenue } = useCardAccountContext(activeCardId)
  const primaryExchange = useAccountPrimaryExchange(activeCardTradeVenue)
  const { data: instrumentListData } = useInstrumentList(primaryExchange)
  const activePageId = useAtomValue(activePageIdAtom)
  const { data: layoutData } = useGetPageLayout(activePageId)
  const { data: watchlists = [] } = useGetWatchlists()
  const [tickerMap, setTickerMap] = useState<
    Map<string, { current: IncrementalTicker | Ticker; prev?: string }>
  >(new Map())

  const watchlistWidget = useMemo(
    () => layoutData?.widgets.find((widget) => widget.widgetType === 'watchlist') ?? null,
    [layoutData?.widgets]
  )
  const watchlistWidgetId = watchlistWidget?.widgetId ?? STATUS_BAR_FALLBACK_WATCHLIST_ID
  const selectedWatchlistIdFromAtom = useAtomValue(watchlistActiveIdAtomFamily(watchlistWidgetId))
  const selectedWatchlistId =
    selectedWatchlistIdFromAtom || watchlistWidget?.config.activeWatchlistId || null
  const selectedWatchlist = useMemo(
    () => watchlists.find((watchlist) => watchlist.watchlistId === selectedWatchlistId) ?? null,
    [selectedWatchlistId, watchlists]
  )
  const topWatchlistEntries = useMemo(
    () => selectedWatchlist?.orderedEntries.slice(0, WATCHLIST_ROW_ITEM_COUNT) ?? [],
    [selectedWatchlist]
  )
  const watchlistEntryExchanges = useMemo(
    () => [...new Set(topWatchlistEntries.map((entry) => entry.exchange))],
    [topWatchlistEntries]
  )
  const enabledWatchlistEntryExchanges = useEnabledExchangeList(watchlistEntryExchanges)
  const watchlistInstruments = useQueries({
    queries: enabledWatchlistEntryExchanges.map((exchange) => ({
      ...instrumentListQueryOptions(exchange),
      refetchInterval: 60 * 60 * 1000,
    })),
    combine: (results) => results.flatMap((result) => result.data?.instruments ?? []),
  })
  const watchlistInstrumentByKey = useMemo(
    () =>
      new Map(
        watchlistInstruments.map((instrument) => [
          watchlistEntryKey(instrument.exchange, instrument.symbol),
          instrument,
        ])
      ),
    [watchlistInstruments]
  )

  const defaultPriceRowInstruments = useMemo<PriceRowInstrument[]>(() => {
    const allInstruments = instrumentListData?.instruments ?? []
    if (primaryExchange === Exchange.EXCHANGE_UPBIT) {
      const priorityOrder = ['BTC', 'ETH', 'USDT']
      const uniqueSpotByBase = new Map<string, (typeof allInstruments)[number]>()

      for (const instrument of allInstruments) {
        if (instrument.kind !== InstrumentKind.INSTRUMENT_KIND_SPOT) continue
        const base = instrument.baseCurrency.toUpperCase()
        if (!uniqueSpotByBase.has(base)) uniqueSpotByBase.set(base, instrument)
      }

      return Array.from(uniqueSpotByBase.entries())
        .sort(([leftBase], [rightBase]) => {
          const leftPriority = priorityOrder.indexOf(leftBase)
          const rightPriority = priorityOrder.indexOf(rightBase)
          if (leftPriority !== -1 && rightPriority !== -1) return leftPriority - rightPriority
          if (leftPriority !== -1) return -1
          if (rightPriority !== -1) return 1
          return 0
        })
        .slice(0, 10)
        .map(([, instrument]) => ({
          symbol: instrument.symbol,
          exchange: instrument.exchange ?? primaryExchange,
        }))
    }

    const allPerps = allInstruments.filter((inst) => inst.symbol.toLowerCase().includes('-perp'))

    const priorityOrder = [
      'BTC',
      'ETH',
      'SOL',
      'XRP',
      'ADA',
      'TRX',
      'AVAX',
      'LINK',
      'DOT',
      'NEAR',
      'MATIC',
    ]

    // Deduplicate by base currency
    const uniqueBaseMap = new Map<string, (typeof allPerps)[0]>()
    for (const inst of allPerps) {
      const base = inst.baseCurrency.toUpperCase()
      // Prefer Linear (USDC) if both exist, or just take the first one
      if (!uniqueBaseMap.has(base) || inst.symbol.includes('USDC')) {
        uniqueBaseMap.set(base, inst)
      }
    }

    return Array.from(uniqueBaseMap.values())
      .sort((a, b) => {
        const aBase = a.baseCurrency.toUpperCase()
        const bBase = b.baseCurrency.toUpperCase()
        const aIdx = priorityOrder.indexOf(aBase)
        const bIdx = priorityOrder.indexOf(bBase)
        if (aIdx > -1 && bIdx > -1) return aIdx - bIdx
        if (aIdx > -1) return -1
        if (bIdx > -1) return 1
        return 0
      })
      .slice(0, 40)
      .map((inst) => ({
        symbol: inst.symbol,
        exchange: inst.exchange || primaryExchange || DEFAULT_VENUE_EXCHANGE,
      }))
  }, [instrumentListData, primaryExchange])

  const customPriceRowInstruments = useMemo<PriceRowInstrument[]>(() => {
    if (!selectedWatchlist) return []

    const seen = new Set<string>()

    return topWatchlistEntries.flatMap((entry) => {
      const key = watchlistEntryKey(entry.exchange, entry.symbol)
      if (seen.has(key)) return []
      seen.add(key)

      const instrument = watchlistInstrumentByKey.get(key)
      if (!instrument) return []

      // Exclude options — the row only shows spot/perp/future price ticks.
      if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) return []

      return {
        symbol: entry.symbol,
        exchange: entry.exchange,
      }
    })
  }, [selectedWatchlist, topWatchlistEntries, watchlistInstrumentByKey])

  const tabGroups = useStatusBarTabGroups(activePageId, layoutData?.widgets)
  // Any group on the page shows the strip, even with no tabs yet, so it can take a dropped tab.
  const hasTabGroups = tabGroups.length > 0
  // Tab groups show no prices, so nothing streams while they're up. A page without any tabs
  // falls back to its watchlist, then the venue's majors, as a static price row.
  const priceRowItems = hasTabGroups
    ? NO_PRICE_ROW_ITEMS
    : selectedWatchlist
      ? customPriceRowInstruments
      : defaultPriceRowInstruments

  // The same ticker can sit in more than one group; subscribe to it once.
  const streamPayloads = useMemo(() => {
    const seen = new Set<string>()
    return priceRowItems.flatMap((inst) => {
      const key = watchlistEntryKey(inst.exchange, inst.symbol)
      if (seen.has(key)) return []
      seen.add(key)
      return {
        symbol: inst.symbol,
        exchange: inst.exchange,
        dataType: MarketDataType.MARKET_DATA_TYPE_TICKER,
      }
    })
  }, [priceRowItems])

  // A glanceable price row, so sub-second price freshness is invisible.
  // Buffer incoming tickers in a ref and flush to React state once per interval —
  // otherwise every WS ticker message triggers a setState + Map copy + commit,
  // which keeps the main thread busy even on an empty dashboard.
  const pendingTickersRef = useRef(new Map<string, Ticker>())

  useMultiMarketDataStream({
    connected: streamPayloads.length > 0,
    payloads: streamPayloads,
    onMessage: (data: TickerList | undefined) => {
      if (!data?.tickers?.length) return
      for (const t of data.tickers) {
        pendingTickersRef.current.set(watchlistEntryKey(t.exchange, t.symbol), t)
      }
    },
  })

  useEffect(() => {
    const flushId = setInterval(() => {
      if (pendingTickersRef.current.size === 0) return
      const pending = pendingTickersRef.current
      pendingTickersRef.current = new Map()

      setTickerMap((prev) => {
        const next = new Map(prev)
        for (const t of pending.values()) {
          const key = watchlistEntryKey(t.exchange, t.symbol)
          const prevData = prev.get(key) as any
          const oldLastPrice = prevData?.current
            ? statusBarPriceOf(prevData.current)
            : prevData?.lastPrice
          const oldPrev = prevData?.prev

          // Only update prev price if it's different from the new price
          const currentPrice = statusBarPriceOf(t)
          const transitionedPrev = oldLastPrice !== currentPrice ? oldLastPrice : oldPrev

          next.set(key, {
            current: t,
            prev: transitionedPrev,
          })
        }
        return next
      })
    }, PRICE_ROW_FLUSH_MS)

    return () => clearInterval(flushId)
  }, [])

  return (
    <div
      className={cn(
        'flex w-full shrink-0 items-center overflow-hidden',
        placement === 'card'
          ? // Painted with the page backdrop so it stays opaque while it rides the bottom of a
            // scrolled page (it is sticky there) yet matches the page around it.
            'bg-page border-foreground/10 h-8 border-t pr-3 pl-2'
          : 'bg-background/85 sticky bottom-0 z-20 h-(--status-bar-height,2.5rem) border-t px-2 pt-0.5 pb-[calc(0.125rem+env(safe-area-inset-bottom))]'
      )}
    >
      {hasTabGroups && activePageId ? (
        <StatusBarTabGroups pageId={activePageId} groups={tabGroups} />
      ) : priceRowItems.length > 0 ? (
        <AssetPriceRow items={priceRowItems} tickerMap={tickerMap} />
      ) : selectedWatchlist ? (
        <div className="flex flex-1 items-center px-4">
          <span className="text-foreground/20 text-xs">{t('noInstrumentsInWatchlist')}</span>
        </div>
      ) : (
        <div className="min-w-0 flex-1" />
      )}
    </div>
  )
}
